import type { IProposal, ProposalId } from '@shared/models/proposals.model';

/**
 * Chooses the nearest remaining proposal when the selected proposal is deleted.
 * Prefer the row that moves into the deleted row's position, then the previous row.
 */
export const getProposalIdAfterDelete = (
  proposals: readonly Pick<IProposal, 'id'>[],
  deletedProposalId: ProposalId,
): ProposalId | undefined => {
  const deletedIndex = proposals.findIndex(
    ({ id }) => id === deletedProposalId,
  );
  const remaining = proposals.filter(({ id }) => id !== deletedProposalId);
  if (remaining.length === 0) return undefined;
  if (deletedIndex < 0) return remaining[0]?.id;
  return remaining[Math.min(deletedIndex, remaining.length - 1)]?.id;
};

/**
 * Next URL proposal after a delete. Unchanged when the deleted row was not
 * selected. `undefined` only when the selected proposal was the last one
 * (explicit baseline), never as a fallback while neighbors remain.
 */
export const getSelectedProposalIdAfterDelete = (
  proposals: readonly Pick<IProposal, 'id'>[],
  deletedProposalId: ProposalId,
  currentlySelectedId: ProposalId | undefined,
): ProposalId | undefined => {
  if (currentlySelectedId !== deletedProposalId) return currentlySelectedId;
  return getProposalIdAfterDelete(proposals, deletedProposalId);
};
